import type { Rule } from '../../../store/types'

// Group rules within a position by tool + common directory prefix.
// Matches the heuristic in native PositionGrantsView: rules are eligible
// for grouping when ≥2 patterns share the same tool and a non-trivial
// directory prefix (≥2 path components). Singletons pass through as
// their own "group" so the caller can render them uniformly.
//
// Example:
//   Read(src/foo/a.ts), Read(src/foo/b.ts), Read(other/x.ts)
// groups into:
//   [ { tool: Read, rules: [a, b], commonPrefix: "src/foo/" },
//     { tool: Read, rules: [x],   commonPrefix: null } ]

export type RuleGroup = {
  tool: string
  rules: Rule[]
  commonPrefix: string | null
  /// Synthetic pattern we'd create if compacted, e.g. "Read(src/foo/**)".
  compactPattern: string | null
}

const MIN_COMPONENTS = 2

export function groupRules(rules: Rule[]): RuleGroup[] {
  // Bucket by action (the legacy tool name).
  const byTool = new Map<string, Rule[]>()
  for (const r of rules) {
    const bucket = byTool.get(r.action) ?? []
    bucket.push(r)
    byTool.set(r.action, bucket)
  }

  const out: RuleGroup[] = []
  for (const [tool, bucket] of byTool) {
    // Extract the argument portion of each predicate (between parens).
    const extracted = bucket
      .map((r) => ({ rule: r, arg: extractArg(r.predicate ?? '') }))
      .filter((e) => e.arg !== null) as {
      rule: Rule
      arg: string
    }[]

    if (extracted.length < 2) {
      // Nothing to group — emit singletons.
      for (const b of bucket) {
        out.push({ tool, rules: [b], commonPrefix: null, compactPattern: null })
      }
      continue
    }

    // Find the longest shared prefix that ends on a `/` boundary.
    const prefix = longestCommonDirPrefix(extracted.map((e) => e.arg))
    if (prefix === null || componentCount(prefix) < MIN_COMPONENTS) {
      // No meaningful shared prefix — emit singletons for now. A future
      // improvement: sub-cluster within the tool by nested prefixes.
      for (const b of bucket) {
        out.push({ tool, rules: [b], commonPrefix: null, compactPattern: null })
      }
      continue
    }

    out.push({
      tool,
      rules: bucket,
      commonPrefix: prefix,
      compactPattern: `${tool}(${prefix}**)`,
    })
  }

  return out
}

/// Extract the text between the first ( and the matching ). Returns null
/// when the pattern doesn't follow `Tool(arg)` shape (e.g. bare wildcards).
function extractArg(pattern: string): string | null {
  const open = pattern.indexOf('(')
  if (open < 0) return null
  const close = pattern.lastIndexOf(')')
  if (close <= open) return null
  return pattern.slice(open + 1, close)
}

function longestCommonDirPrefix(args: string[]): string | null {
  if (args.length === 0) return null
  let prefix = args[0]
  for (let i = 1; i < args.length; i++) {
    prefix = sharedPrefix(prefix, args[i])
    if (!prefix) return null
  }
  // Trim to the last /. A half-component match like "src/fo" isn't useful.
  const lastSlash = prefix.lastIndexOf('/')
  if (lastSlash < 0) return null
  return prefix.slice(0, lastSlash + 1)
}

function sharedPrefix(a: string, b: string): string {
  const n = Math.min(a.length, b.length)
  let i = 0
  while (i < n && a[i] === b[i]) i++
  return a.slice(0, i)
}

function componentCount(path: string): number {
  return path.split('/').filter((p) => p.length > 0).length
}
